
import { Component, Vue } from "vue-property-decorator";
import { SMApp, SMQuestionnaire, SMTasks, SMCases, SMUser, SMTasksServices } from "@/store";
import { API_ROUTES } from "@/utils/API";
import { API_TCase } from "@/models/cases";
import { SUCCESS_POPUP_TYPE } from "@/constants/popupTypes";

import {
  COMPLETED_TASK_STATUS,
  READY_TO_SUBMIT_TASK_STATUS,
  DOCUMENT_TASK_TYPE,
  FLOW_TASK_TYPE,
  GUIDE_TASK_TYPE,
  GUIDE_WITH_CUSTOM_BUTTON_TASK_TYPE,
  INFO_TASK_TYPE,
  SUBMIT_FOR_REVIEW_TASK_TYPE,
} from "@/constants/tasks";

import {
  DATA_AND_DOCUMENTS_SUBMISSION_SERVICE_STEP,
  ID_CHECKS_SERVICE_STEP,
  ADDITIONAL_DATA_AND_DOCUMENTS_SERVICE_STEP,
  DATA_AND_DOCUMENT_SUBMISSION_SERVICE_STEP,
  DATA_SUBMISSION_SERVICE_STEP,
  REPORT_CORRECTION_SERVICE_STATUS,
  ELIGIBILITY_CHECK_SERVICE_TYPE,
  ADDITIONAL_INFO_FOR_EXPERT_SERVICE_STEP,
} from "@/constants/services";

const STATUSES = {
  [COMPLETED_TASK_STATUS]: COMPLETED_TASK_STATUS,
  [READY_TO_SUBMIT_TASK_STATUS]: READY_TO_SUBMIT_TASK_STATUS,
};

const CANDIDATE_ONLINE_APPLICATION_ROUTE = "CandidateOnlineApplication";

const ONLINE_APPLICATION_TAB_TYPE = "online_application";

const COS_TAB_NAME = "COS";

enum aliasType {
  NAME_ALIAS = "name",
  EMAIL_ALIAS = "email",
  PASSWORD_ALIAS = "password",
}

const ALIAS = aliasType.EMAIL_ALIAS;

const CASE_PRODUCT_TYPE = "case";

const CASES_PATH = "/cases";
const DASHBOARD = "dashboard";

Component.registerHooks(["beforeRouteEnter"]);

@Component({
  components: {
    ActionButtons: () => import("@/components/ActionContainer/ActionButtons.vue"),
    ActionContainerCard: () => import("@/components/ActionContainer/ActionContainerCard.vue"),
    TaskDocumentTemplate: () => import("@/modules/Documents/TaskDocumentTemplate.vue"),
    DocumentActionContainerCard: () =>
      import("@/modules/Documents/DocumentActionContainerCard.vue"),
    DocumentTaskTemplate: () => import("@/modules/Documents/DocumentTaskTemplate.vue"),
  },
})
export default class CandidateTasksTemplate extends Vue {
  loading = false;

  statuses = STATUSES;

  alias = ALIAS;

  successPopupType = SUCCESS_POPUP_TYPE;

  isChecked = false;

  get isNotInfoTaks() {
    return !this.isInfoTask;
  }

  get showActionButtons() {
    if (this.isDocumentTask) return false;

    return this.isNotInfoTaks;
  }

  get okButtonDisabled() {
    if (this.showReadTheTaskConfirmation) return !this.isChecked;

    return false;
  }

  get onlineApplicationTab() {
    return SMCases.caseTabs.find(
      (tab) => tab.tab_type === ONLINE_APPLICATION_TAB_TYPE && tab.tab_name !== COS_TAB_NAME
    );
  }

  get taskHasNotCompletedStatus() {
    return this.taskTemplate?.status !== this.statuses.completed;
  }

  get stepName() {
    return (SMCases.case as API_TCase).case_progress_step?.name?.trim();
  }

  get isEnableForStartTaskStep() {
    return [
      DATA_SUBMISSION_SERVICE_STEP,
      DATA_AND_DOCUMENT_SUBMISSION_SERVICE_STEP,
      DATA_AND_DOCUMENTS_SUBMISSION_SERVICE_STEP,
      ADDITIONAL_DATA_AND_DOCUMENTS_SERVICE_STEP,
      ADDITIONAL_INFO_FOR_EXPERT_SERVICE_STEP,
      ID_CHECKS_SERVICE_STEP,
    ].includes(this.stepName);
  }

  get isReportCorrectionCaseStatus() {
    return (SMCases.case as API_TCase).status === REPORT_CORRECTION_SERVICE_STATUS;
  }

  get isTaskCreatedForUser() {
    return SMUser.user?.role === this.taskTemplate?.user_role;
  }

  get isEligibilityCheckService() {
    return (SMCases.case as API_TCase).case_template?.name === ELIGIBILITY_CHECK_SERVICE_TYPE;
  }

  get isOkButtonShow() {
    if (this.isGuideWithCustomButtonTask) return true;

    if (SMTasks.taskTemplate.hide_button) return false;

    if (SMUser.isExpert) return this.taskHasNotCompletedStatus;

    if (this.isEligibilityCheckService) {
      if (
        SMUser.isEmployer &&
        this.isReportCorrectionCaseStatus &&
        this.isEmployerHasToDoTasksInCases
      ) {
        return this.isTaskCreatedForUser;
      }

      if (
        (SMUser.isCandidate || SMUser.isEmployee) &&
        this.isReportCorrectionCaseStatus &&
        this.isCandidateHasToDoTasksInCases
      ) {
        return this.isTaskCreatedForUser;
      }
    }

    return (
      this.isEnableForStartTaskStep && this.taskHasNotCompletedStatus && this.isTaskCreatedForUser
    );
  }

  startForReview() {
    const tabTypeId = String(this.onlineApplicationTab?.tab_type_id || "");

    if (!tabTypeId) return;

    const id = localStorage.getItem("case_id") || String(SMCases.caseId);

    this.$router.push({
      name: CANDIDATE_ONLINE_APPLICATION_ROUTE,
      params: {
        id,
        tabTypeId,
      },
    });
  }

  get caseId() {
    return Number(SMCases.caseId ?? 0);
  }

  get successPopup() {
    return SMTasks.successPopup;
  }

  closeSuccessPopup() {
    const alternativePath =
      SMUser.isEmployer && this.caseId
        ? `${CASES_PATH}/${this.caseId}/${DASHBOARD}/${SMCases.tabId}`
        : CASES_PATH;

    const previousPath = SMApp.previousRouterHistoryPath || alternativePath;

    this.$router.push(previousPath);
  }

  get taskTemplate() {
    return SMTasks.taskTemplate;
  }

  get status() {
    return this.taskTemplate?.status;
  }

  get type() {
    return this.taskTemplate?.type;
  }

  get isDocumentTask() {
    return this.type === DOCUMENT_TASK_TYPE;
  }

  get isFlowTask() {
    return this.type === FLOW_TASK_TYPE;
  }

  get isGuideTask() {
    return this.type === GUIDE_TASK_TYPE;
  }

  get isGuideWithCustomButtonTask() {
    return this.type === GUIDE_WITH_CUSTOM_BUTTON_TASK_TYPE;
  }

  get isInfoTask() {
    return this.type === INFO_TASK_TYPE;
  }

  get isSubmitForReviewTask() {
    return this.type === SUBMIT_FOR_REVIEW_TASK_TYPE;
  }

  get showReadTheTaskConfirmation() {
    return this.isGuideTask && this.isOkButtonShow;
  }

  get confirmCheckboxClasses() {
    if (SMUser.isExpert) return {};

    return {
      ["checkbox_bold"]: true,
    };
  }

  get okLabel() {
    if (this.isGuideWithCustomButtonTask) return this.taskTemplate?.button_name || "";

    if (this.isGuideTask) return this.$root.$t("caption.complete");

    if (this.isDocumentTask) return this.$root.$t("caption.save");

    return this.$root.$t("caption.start");
  }

  get showOkButton() {
    return false;
  }

  async resolveGuideWithCustomButtonTask() {
    const url = this.taskTemplate?.button_url;

    if (!url) return;

    try {
      await this.$router.push(url);
    } catch {
      return;
    }
  }

  async onTaskOk() {
    if (!this.taskTemplate) return;

    if (this.isGuideWithCustomButtonTask) {
      await this.resolveGuideWithCustomButtonTask();

      return;
    }

    this.loading = true;

    const { id } = this.taskTemplate;

    if (this.isGuideTask) await SMTasks.finishTask(id);

    if (this.isFlowTask && SMTasks.taskUserflow !== null) {
      await SMQuestionnaire.openQuestionnaire(SMTasks.taskUserflow);
    }

    if (this.isDocumentTask) {
      return;
      // await this.$router.push(`/cases/${SMCases.currentCaseId}/documents/${SMCases.documentId}`);
    }

    if (this.isSubmitForReviewTask) this.startForReview();

    this.loading = false;
  }

  get taskId(): number {
    return Number(this.$route.params.taskId ?? 0);
  }

  get taskStatus() {
    return this.$route.params.status;
  }

  get isCaseProduct() {
    return SMCases.caseId || this.taskTemplate?.product_type === CASE_PRODUCT_TYPE;
  }

  async checkCaseIsSet() {
    if (SMCases.caseId || this.taskTemplate?.product_type !== CASE_PRODUCT_TYPE) return;

    const { data } = await this.axios.get(API_ROUTES.CASE_ID(this.taskTemplate.product_type_id));

    if (!data) return;

    SMCases.setCaseId(data.ID);

    SMCases.setCase(data);
  }

  async getTasksForUsers() {
    if (this.isCaseProduct) {
      await SMTasksServices.getTableTasksToDo(SMCases.caseId);
    } else {
      await SMTasks.getTableTasksToDo();
    }
  }

  async getTaskTemplate() {
    await SMTasks.getTaskTemplate(this.taskId);

    if (!this.taskTemplate) return;

    await this.checkCaseIsSet();

    await this.getTasksForUsers();

    const { name } = this.taskTemplate;

    if (name) SMApp.setPageTitle(name);
  }

  created() {
    this.getTaskTemplate();
  }

  beforeDestroy() {
    SMTasks.unsetTaskTemplate();
  }

  rollbackSuccessPopup() {
    SMTasks.closeSuccessPopup();
  }

  async beforeRouteEnter(to: any, from: any, next: any) {
    next((vm: any) => {
      vm.rollbackSuccessPopup();
    });
  }

  get isEmployerHasToDoTasksInCases() {
    if (this.isCaseProduct) {
      const tasks = SMTasksServices.employerTasksToDo;

      return !!tasks?.length;
    } else {
      const tasks = SMTasks.employerTasksToDo;

      return !!tasks?.filter((task) => task.productType === CASE_PRODUCT_TYPE).length;
    }
  }

  get isCandidateHasToDoTasksInCases() {
    if (this.isCaseProduct) {
      const tasks = SMTasksServices.candidateTasksToDo;

      return !!tasks?.length;
    } else {
      const tasks = SMTasks.candidateTasksToDo;

      return !!tasks?.filter((task) => task.productType === CASE_PRODUCT_TYPE).length;
    }
  }
}
